import React, { useState, useEffect } from 'react';
import { Grid } from '@material-ui/core';
import { Tabs, Tab, Box } from '@mui/material';
import AthenaReports from '../components/AthenaReportsv2';
import { AntTabs, AntTab } from '../components/common/Tabs';
import PrintManagerDynamo from '../components/PrintManagerDynamo';
import { REPORT_SYSTEMS } from '../config/report-systems';
import LambdaFetch from '../functions/FetchFromLambda';

const PRINT_MANAGER_HASH = 'print_manager';
const TOP_REPORTS = 0;
const TOP_PRINT = 1;

// Lookup from catalog erp_source value → display metadata from config
const SYSTEM_LOOKUP = Object.fromEntries(
  REPORT_SYSTEMS.map(s => [s.sourceKey, s])
);

const ReportsModule = ({ ...props }) => {
  const [topTab, setTopTab] = useState(TOP_REPORTS)
  const [systemTabIndex, setSystemTabIndex] = useState(0)
  const [visibleSystems, setVisibleSystems] = useState([])
  const [systemsLoaded, setSystemsLoaded] = useState(false)

  const { allow } = props.fetchInitialData.credentials
  const color = props.fetchInitialData.credentials.primaryAppColor

  // Discover ERP systems from catalog, then filter by reportSystem permission
  useEffect(() => {
    async function discoverSystems() {
      try {
        const resp = await LambdaFetch(
          'reports-athena',
          'post',
          props.fetchInitialData.credentials.user.accessToken,
          JSON.stringify({ action: 'get-all-reports-catalog' }),
          '',
          props.fetchInitialData.credentials
        )
        if (resp.success) {
          // Collect distinct erp_source values in catalog order
          const seen = new Set()
          const sources = []
          for (const report of resp.data.catalog) {
            if (report.erp_source && !seen.has(report.erp_source)) {
              seen.add(report.erp_source)
              sources.push(report.erp_source)
            }
          }

          // Resolve display info from REPORT_SYSTEMS lookup; fall back to raw sourceKey.
          // Sort by REPORT_SYSTEMS config order so tab order is controlled in one place.
          const systems = sources
            .map(src => SYSTEM_LOOKUP[src] || { sourceKey: src, id: src, label: src })
            .filter(sys => allow('reportSystem', sys.id))
            .sort((a, b) => {
              const ai = REPORT_SYSTEMS.findIndex(s => s.id === a.id)
              const bi = REPORT_SYSTEMS.findIndex(s => s.id === b.id)
              return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi)
            })

          setVisibleSystems(systems)

          // Restore deep-link hash after systems are known
          const hash = window.location.hash.slice(1)
          if (hash === PRINT_MANAGER_HASH) {
            setTopTab(TOP_PRINT)
          } else {
            const idx = systems.findIndex(s => s.hashKey === hash || s.sourceKey === hash)
            if (idx !== -1) setSystemTabIndex(idx)
          }
        }
      } catch (_) {
        // Network/parse error — fall back to config list filtered by permission
        setVisibleSystems(REPORT_SYSTEMS.filter(sys => allow('reportSystem', sys.id)))
      } finally {
        setSystemsLoaded(true)
      }
    }
    discoverSystems()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (systemsLoaded && visibleSystems.length === 0 && topTab === TOP_PRINT) {
      setTopTab(TOP_REPORTS)
      window.location.hash = ''
    }
  }, [systemsLoaded, visibleSystems, topTab])

  const handleTopTabChange = (_, val) => {
    setTopTab(val)
    if (val === TOP_PRINT) {
      window.location.hash = PRINT_MANAGER_HASH
    } else {
      const sys = visibleSystems[systemTabIndex]
      window.location.hash = sys?.hashKey || sys?.sourceKey || ''
    }
  }

  const handleSystemTabChange = (_, val) => {
    setSystemTabIndex(val)
    const sys = visibleSystems[val]
    window.location.hash = sys?.hashKey || sys?.sourceKey || ''
  }

  return (
    <div style={{ padding: '0 0.5rem' }}>
      <Grid item xs={12} sm={12} md={12}>

        {/* Top-level tabs: Reports | Print Manager */}
        <AntTabs
          color={color}
          value={topTab}
          onChange={handleTopTabChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="reports top tabs"
        >
          <AntTab color={color} label="Reports" value={TOP_REPORTS} />
          {(!systemsLoaded || visibleSystems.length > 0) && (
            <AntTab color={color} label="Print Manager" value={TOP_PRINT} />
          )}
        </AntTabs>

        <div style={{ position: 'relative', marginTop: '1.5rem' }}>

          {topTab === TOP_REPORTS && (
            <>
              {/* ERP system sub-tabs — only rendered when 2+ systems are visible */}
              {visibleSystems.length >= 2 && (
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs
                    value={systemTabIndex}
                    onChange={handleSystemTabChange}
                    textColor='primary'
                    indicatorColor='primary'
                  >
                    {visibleSystems.map((sys, idx) => (
                      <Tab key={sys.id || sys.sourceKey} label={sys.label} value={idx} />
                    ))}
                  </Tabs>
                </Box>
              )}

              {systemsLoaded && visibleSystems.length === 0 && (
                <div style={{ textAlign: 'center', marginTop: '4rem', color: '#666' }}>
                  You don't have permission to view reports. Contact your administrator.
                </div>
              )}

              {systemsLoaded && visibleSystems.map((sys, idx) =>
                systemTabIndex === idx && (
                  <AthenaReports
                    key={sys.sourceKey}
                    {...props}
                    system={sys.sourceKey}
                  />
                )
              )}
            </>
          )}

          {topTab === TOP_PRINT && (
            <PrintManagerDynamo
              {...props}
              accessToken={props.fetchInitialData.credentials.user.accessToken}
            />
          )}

        </div>
      </Grid>
    </div>
  )
}

export default ReportsModule
